import React from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormLabel,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Controller } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import * as yup from "yup";

import { MealKind } from "@/gql/graphql";
import useFormWithYup from "@/hooks/useFormWithYup";
import { humanize } from "@/utils/string";

import MealTypeIcon from "./MealTypeIcon";

interface Props {
  mealOptions: MealKind[];
  open: boolean;
  onClose: () => void;
  onSubmit: (values: FormValues) => void;
}

interface FormValues {
  mealType: MealKind;
}

const useStyles = makeStyles()((theme) => ({
  content: {
    display: "flex",
    flexDirection: "column",
    minWidth: 500,
  },
  actions: {
    justifyContent: "center",
    padding: theme.spacing(2, 8, 8),
  },
  closeButton: {
    backgroundColor: "#F2F2F2",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    "& .MuiFormLabel-root": {
      marginBottom: theme.spacing(3),
    },
  },
}));

const validationSchema = yup
  .object({
    mealType: yup.mixed().oneOf(Object.values(MealKind)).required(),
  })
  .defined();

const AddNewMealType: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  mealOptions,
}) => {
  const { classes } = useStyles();

  const { control, handleSubmit, setValue } = useFormWithYup<FormValues>({
    defaultValues: { mealType: "" as MealKind },
    validationSchema: validationSchema,
  });

  const _onSubmit = (values: FormValues) => {
    onSubmit(values);
    _onClose();
  };

  const _onClose = () => {
    setValue("mealType", "" as MealKind);
    onClose();
  };

  return (
    <Dialog open={open} onClose={_onClose}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <span> Add new meal type</span>
          <IconButton
            className={classes.closeButton}
            size="small"
            onClick={onClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <form onSubmit={handleSubmit(_onSubmit)}>
        <DialogContent className={classes.content}>
          <Controller
            name="mealType"
            control={control}
            render={({ field, fieldState }) => (
              <div className={classes.field}>
                <FormLabel error={!!fieldState.error}>Meal type</FormLabel>
                <TextField {...field} select>
                  <MenuItem disabled value="">
                    Choose meal type
                  </MenuItem>
                  {mealOptions.map((mealOption) => (
                    <MenuItem key={mealOption} value={mealOption}>
                      <Box display="flex" alignItems="center">
                        <MealTypeIcon size="large" type={mealOption} />
                        <Box marginX={2} />
                        <Typography>{humanize(mealOption)}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </div>
            )}
          />
        </DialogContent>
        <DialogActions className={classes.actions}>
          <Button type="submit">Add</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddNewMealType;
